import IconButton from '@/components/UI/buttons/IconButton'
import { ColorEnum } from '@/constants/color'
import { Layout } from '@/features/layout/types'
import BackArrowIcon from '@/features/ui/icons/components/BackArrowIcon'
import CalendarIcon from '@/features/ui/icons/components/CalendarIcon'
import PersonIcon from '@/features/ui/icons/components/PersonIcon'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface HeaderProps {
    closedAt?: string
    layout: Layout
    onBack?: () => void
    profileName?: string
    title?: string
}

const Header: FC<HeaderProps> = ({ closedAt, layout, onBack, profileName, title }) => {
    const { t } = useTranslation()
    const { styles } = useStyles(stylesheet, { layout })

    return (
        <View style={styles.container}>
            <View style={styles.titleRow}>
                <Typography.Heading
                    variant={layout === 'compact' ? 'h2' : 'h1'}
                    style={styles.heading}
                >
                    {title ?? t('shiftCloseouts.title')}
                </Typography.Heading>

                {layout === 'extended' && onBack && (
                    <IconButton
                        accessibilityLabel={t('actions.back')}
                        Icon={() => <BackArrowIcon color={ColorEnum.GRAY_900} />}
                        onPress={onBack}
                    />
                )}
            </View>

            {(closedAt || profileName) && (
                <View style={styles.metadata} testID="shift-closeout-header-metadata">
                    {closedAt && (
                        <View style={styles.metadataItem}>
                            <CalendarIcon height={16} width={16} />
                            <Typography.Description style={styles.metadataText}>
                                {closedAt}
                            </Typography.Description>
                        </View>
                    )}

                    {profileName && (
                        <View style={styles.metadataItem}>
                            <PersonIcon fill={ColorEnum.GRAY_600} />
                            <Typography.Description style={styles.metadataText}>
                                {profileName}
                            </Typography.Description>
                        </View>
                    )}
                </View>
            )}
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    container: {
        gap: 4,
        padding: 16,
        borderBottomColor: ColorEnum.GRAY_200,
        borderBottomWidth: 1,
        variants: {
            layout: {
                compact: {
                    paddingLeft: 16 + insets.left,
                    paddingRight: 16 + insets.right,
                },
                extended: {},
            },
        },
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
    },
    heading: {
        flexShrink: 1,
        color: ColorEnum.GRAY_900,
    },
    metadata: {
        gap: 8,
        variants: {
            layout: {
                compact: {
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                },
                extended: {
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    columnGap: 16,
                },
            },
        },
    },
    metadataItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    metadataText: {
        color: ColorEnum.GRAY_600,
    },
}))

export default Header
